import { useMemo } from 'react'

import { useQuery } from '@apollo/client'
import { OneDataCollection as DataCollection } from '@factorialco/factorial-one-react/dist/experimental'
import {
  ImportProcessSchemaAdapterDocument,
  UniversalImporterImportProcessMetadata,
  UniversalImporterImportProcessMetadataInput,
  UniversalImporterProcessDetailsDocument,
  UniversalImporterProcessDetailsQuery,
  UniversalImporterProcessDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import i18n from 'modules/core/lib/i18n'

export const DEFAULT_COLUMN_VALUE = '-'

type UniversalImporterNuvoDropdownOption = {
  label: string
  value: string
}

interface ImportProcessDataCollectionProps {
  setSelectedRowColumns: (columns: Record<string, string>) => void
  setSelectedRowErrors: (errors: string) => void
  setIsModalOpen: (isOpen: boolean) => void
  importProcessId: string
}

export const ImportProcessDataCollection = ({
  setSelectedRowColumns,
  setSelectedRowErrors,
  setIsModalOpen,
  importProcessId,
}: ImportProcessDataCollectionProps) => {
  const { company } = useCurrentSession()

  const parseColumnValue = (
    columnType: string,
    value: unknown,
    options: UniversalImporterNuvoDropdownOption[]
  ): string => {
    if (!value) return DEFAULT_COLUMN_VALUE

    let result: string
    switch (columnType) {
      case 'category':
        const option = options?.find((opt) => opt.value === value)
        result = option?.label ?? String(value)
        break
      case 'float':
        result = parseFloat(String(value)).toString()
        break
      case 'boolean':
        result = value ? i18n.t('common.yes') : i18n.t('common.no')
        break
      default:
        result = String(value)
    }

    return result.length > 30 ? `${result.slice(0, 30)}...` : result
  }

  const { data: importProcessData } = useQuery(UniversalImporterProcessDocument, {
    variables: { importProcessId: Number(importProcessId) },
  })

  const importType = importProcessData?.universalImporter.importProcess?.importType ?? ''
  const legalEntityId = importProcessData?.universalImporter.importProcess?.legalEntity?.id
  const metadata: UniversalImporterImportProcessMetadata[] | undefined =
    importProcessData?.universalImporter.importProcess?.metadata || undefined

  const metadataInput: UniversalImporterImportProcessMetadataInput[] = useMemo(() => {
    if (metadata && metadata.length > 0) {
      return metadata.map((item: UniversalImporterImportProcessMetadata) => ({
        key: item.key,
        values: item.values,
        type: item.type,
      }))
    }

    if (legalEntityId) {
      return [
        {
          key: 'legal_entity_ids',
          values: [String(legalEntityId)],
          type: 'integer',
        },
      ]
    }

    return []
  }, [metadata, legalEntityId])

  const { data: schemaAdapterData } = useQuery(ImportProcessSchemaAdapterDocument, {
    variables: {
      companyId: company.id,
      importType,
      metadata: metadataInput,
    },
  })

  const source = useGraphqlDataSource({
    document: UniversalImporterProcessDetailsDocument,
    itemOnClick: (row) => () => {
      setIsModalOpen(true)
      setSelectedRowErrors(row._errors ?? '')
      const { _errors, _row_uuid, ...columns } = row
      const formattedData: Record<string, string> =
        schemaAdapterData?.universalImporter?.schemaAdaptersConnection?.nodes?.reduce(
          (acc, node) => ({
            ...acc,
            [node.label]: parseColumnValue(
              node.columnType ?? '',
              columns[node.key] ?? '',
              node.dropdownOptions ?? []
            ),
          }),
          {}
        ) ?? {}
      setSelectedRowColumns(formattedData)
    },
    mapVariables: () => ({
      importType,
      processId: Number(importProcessId),
      companyId: company.id,
      status: ['errored'],
    }),
    mapData: (data: UniversalImporterProcessDetailsQuery) => {
      const rowsData: Record<string, string>[] =
        data?.universalImporter?.importProcessRowsConnection?.nodes?.flatMap(
          (node) =>
            node?.rows?.map((row) => {
              const rowColumns: Record<string, string> =
                row?.columns?.reduce(
                  (acc, column) => ({
                    ...acc,
                    [column.key]: column.value,
                  }),
                  {}
                ) ?? {}

              rowColumns._errors =
                row?.errors
                  ?.map((error) => {
                    // clean strings like: {:messages=>["some message"]}
                    return error
                      .replace(/^\{.*?=>\[/, '')
                      .replace(/\]\}$/, '')
                      .replace(/^"|"$/g, '')
                  })
                  .join(', ') ?? ''

              return rowColumns
            }) ?? []
        ) ?? []
      return rowsData
    },
    mapPagination: (data: UniversalImporterProcessDetailsQuery) => ({
      total: data.universalImporter?.importProcessRowsConnection?.totalCount ?? 0,
    }),
  })

  const dynamicColumns =
    schemaAdapterData?.universalImporter?.schemaAdaptersConnection?.nodes.slice(0, 3)

  return (
    <DataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t(
                  'universal_importer.import_history_process.data_collection.columns.status'
                ),
                render: (row) => {
                  const status = (row._errors || []).length < 1 ? 'positive' : 'critical'
                  return {
                    type: 'status',
                    value: {
                      status,
                      label:
                        status === 'positive'
                          ? i18n.t('universal_importer.import_history_process.status.positive')
                          : i18n.t('universal_importer.import_history_process.status.critical'),
                    },
                  }
                },
              },
              {
                label: i18n.t(
                  'universal_importer.import_history_process.data_collection.columns.errors'
                ),
                render: (_row) => {
                  const errorMessage = _row._errors || ''
                  return {
                    type: 'text',
                    value:
                      errorMessage.length > 40 ? `${errorMessage.slice(0, 40)}...` : errorMessage,
                  }
                },
              },
              ...(dynamicColumns?.map((column) => ({
                label: column.label,
                render: (row: Record<string, string>) => ({
                  type: 'text' as const,
                  value: parseColumnValue(
                    column.columnType ?? '',
                    row[column.key] ?? '',
                    column.dropdownOptions ?? []
                  ),
                }),
              })) ?? []),
            ],
            frozenColumns: 1,
          },
        },
      ]}
    />
  )
}
